import { z } from "zod";
import { AudioSummarySchema } from "./telemetry.js";
import { HoursOfOperationDetailSchema, InventoryTypeSchema } from "./configuration.js";
import { FlowFindingSchema } from "./flow-lint.js";

/**
 * Every POST from a customer-account Lambda to the central ingestion API
 * uses this envelope. `friendly_name` / `aws_account_number` are
 * informational only — the ingestion Lambda resolves the authoritative
 * `environment_id` from the caller's API key and cross-checks these fields
 * against the stored record rather than trusting them. See
 * documentation/design.md.
 *
 * ADR-0005: nothing destination-specific (case IDs, ticket IDs, backend
 * names) may ever appear here. This shape is baked into every deployed
 * customer stack and cannot be updated once it is in their account.
 */
export const EventTypeSchema = z.enum([
  "cloudwatch_alarm",
  "trusted_advisor",
  "support_case",
  "service_quota",
  "concurrency_metrics",
  "journey_rollup",
  "connect_inventory",
  "contact_record",
  "contact_voice",
  "heartbeat",
  "usage",
  "test",
]);
export type EventType = z.infer<typeof EventTypeSchema>;

export const SeveritySchema = z.enum(["critical", "warning", "info"]);
export type Severity = z.infer<typeof SeveritySchema>;

export const AlarmStateSchema = z.enum(["ALARM", "OK", "INSUFFICIENT_DATA"]);
export type AlarmState = z.infer<typeof AlarmStateSchema>;

export const CloudWatchAlarmPayloadSchema = z.object({
  alarmName: z.string().min(1),
  alarmArn: z.string().min(1),
  region: z.string().min(1),
  state: AlarmStateSchema,
  previousState: AlarmStateSchema.optional(),
  // CloudWatch's own format, e.g. "2026-08-29T11:59:30.000+0000": an offset without the colon zod's ISO check requires.
  stateChangeTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/, "CloudWatch timestamp"),
  reason: z.string().max(2048).optional(),
  metricName: z.string().optional(),
  namespace: z.string().optional(),
  dimensions: z.record(z.string(), z.string()).optional(),
  /** Best single identifier for the affected resource, e.g. a Connect instance ID. */
  resource: z.string().optional(),
});
export type CloudWatchAlarmPayload = z.infer<typeof CloudWatchAlarmPayloadSchema>;

export const TrustedAdvisorPayloadSchema = z.object({
  checkId: z.string(),
  checkName: z.string(),
  category: z.string().optional(),
  status: z.enum(["ok", "warning", "error", "not_available"]),
  flaggedResourceCount: z.number().int().nonnegative().optional(),
  /** Deterministic per (check, status, flagged count): a re-poll with no change is a duplicate, not a new finding. */
  observedAt: z.string().datetime({ offset: true }).optional(),
});
export type TrustedAdvisorPayload = z.infer<typeof TrustedAdvisorPayloadSchema>;

export const SupportCasePayloadSchema = z.object({
  caseId: z.string(),
  displayId: z.string().optional(),
  subject: z.string(),
  status: z.string(),
  severity: z.string().optional(),
  service: z.string().optional(),
  timeCreated: z.string().optional(),
  lastUpdated: z.string(),
  lastCommunications: z
    .array(
      z.object({
        body: z.string(),
        submittedBy: z.string().optional(),
        timeCreated: z.string(),
      }),
    )
    .max(2),
  /** Poll-level marker so the router can replace the cache: all cases from one poll share observedAt. */
  observedAt: z.string().datetime({ offset: true }).optional(),
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
});
export type SupportCasePayload = z.infer<typeof SupportCasePayloadSchema>;

/**
 * How a quota's current usage was measured. Mirrors the three mechanisms
 * in design.md "Service quota polling mechanism"; `unavailable` means the
 * limit is known but nothing measures usage for it.
 */
export const QuotaUsageSourceSchema = z.enum([
  "resource_count",
  "cloudwatch_usage",
  "native_metric",
  "unavailable",
  /** A throughput limit (N per second): no usage is measured, and no increase is ever recommended - the remedy is caching or queueing. */
  "rate",
]);
export type QuotaUsageSource = z.infer<typeof QuotaUsageSourceSchema>;

export const ServiceQuotaItemSchema = z.object({
  quotaCode: z.string().min(1),
  quotaName: z.string().min(1),
  value: z.number(),
  unit: z.string().optional(),
  adjustable: z.boolean(),
  usage: z.number().nonnegative().optional(),
  usageSource: QuotaUsageSourceSchema,
});
export type ServiceQuotaItem = z.infer<typeof ServiceQuotaItemSchema>;

/**
 * A poll run is split into chunks (WAF body limit); all chunks share
 * `observedAt`, and `chunk` tells the router which one is last so it can
 * drop rows this run did not refresh (always-replace semantics).
 */
export const ServiceQuotaPayloadSchema = z.object({
  serviceCode: z.string().min(1),
  observedAt: z.string().datetime({ offset: true }),
  quotas: z.array(ServiceQuotaItemSchema).max(500),
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
});
export type ServiceQuotaPayload = z.infer<typeof ServiceQuotaPayloadSchema>;

/**
 * ADR-0004: a compact metric series pushed on the quota poller's
 * schedule. ~72 points per metric per 6h run at 5-minute resolution.
 */
/**
 * The instance-level concurrency metrics Amazon Connect emits, by their
 * real names (adminguide monitoring-cloudwatch.html): the voice pair is
 * ConcurrentCalls / ConcurrentCallsPercentage, not "ConcurrentActive…".
 * Percentages are pushed as 0-100 - the poller scales the calls one,
 * which Connect emits as a 0-1 fraction, so the portal never has to know.
 */
export const ConcurrencyMetricSchema = z.enum([
  "ConcurrentCalls",
  "ConcurrentCallsPercentage",
  "ConcurrentActiveChats",
  "ConcurrentActiveChatsPercentage",
  "ConcurrentTasks",
  "ConcurrentTasksPercentage",
  "ConcurrentEmails",
  "ConcurrentEmailsPercentage",
]);
export type ConcurrencyMetric = z.infer<typeof ConcurrencyMetricSchema>;

export const ConcurrencyMetricsPayloadSchema = z.object({
  observedAt: z.string().datetime({ offset: true }),
  points: z
    .array(
      z.object({
        metric: ConcurrencyMetricSchema,
        timestamp: z.string().datetime({ offset: true }),
        value: z.number().nonnegative(),
      }),
    )
    .max(1000),
});
export type ConcurrencyMetricsPayload = z.infer<typeof ConcurrencyMetricsPayloadSchema>;

/**
 * ADR-0003 Track A: pre-aggregated contact-flow edges for one window and
 * one filter slice. Nodes are keyed on flow + block id, never block name.
 */
export const JourneyDimensionSchema = z.enum(["all", "channel", "queue", "dnis", "flow", "voice"]);
export type JourneyDimension = z.infer<typeof JourneyDimensionSchema>;

export const JourneyEdgeSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  fromLabel: z.string().min(1),
  toLabel: z.string().min(1),
  /** The branch the source block took to reach the target - a pressed key, "Timeout", "Error" - when the log says. */
  branch: z.string().max(64).optional(),
  count: z.number().int().nonnegative(),
  avgDwellMs: z.number().nonnegative().optional(),
  errorCount: z.number().int().nonnegative(),
  /** A few recent contacts on this edge: the bridge from the Sankey into the single-call view (requirements.md section 8). */
  sampleContactIds: z.array(z.string()).max(20).optional(),
});
export type JourneyEdge = z.infer<typeof JourneyEdgeSchema>;

export const JourneyRollupPayloadSchema = z.object({
  windowStart: z.string().datetime({ offset: true }),
  windowEnd: z.string().datetime({ offset: true }),
  dimension: JourneyDimensionSchema,
  /** Slice value for the dimension, e.g. "VOICE", "Sales", "+18005551234"; "*" for `all`. */
  slice: z.string().min(1),
  contactCount: z.number().int().nonnegative(),
  edges: z.array(JourneyEdgeSchema).max(2000),
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
});
export type JourneyRollupPayload = z.infer<typeof JourneyRollupPayloadSchema>;

/**
 * #349: the Polly voice a flow's Set voice block chose, and its engine
 * (`standard` / `neural` / `generative` / `long-form`, lower-case on the
 * wire). Configuration, not CX content (ADR-0006): the id the customer
 * picked in the block. The `voice` journey slice is `voiceSlice()` of it;
 * a voice contact no Set voice block had reached yet heard the instance's
 * default and is sliced `default`.
 */
export const VoiceHeardSchema = z.object({
  voice: z.string().min(1).max(64),
  engine: z.string().regex(/^[a-z-]{1,32}$/).optional(),
});
export type VoiceHeard = z.infer<typeof VoiceHeardSchema>;
export const DEFAULT_VOICE_SLICE = "default";
export const voiceSlice = (v: VoiceHeard | undefined): string => (v ? (v.engine ? `${v.voice} (${v.engine})` : v.voice) : DEFAULT_VOICE_SLICE);

/**
 * #349: per window, the voice each contact heard last, from its flow log
 * lines. Central sets it on the contact's `Contacts` row (a row not landed
 * yet is skipped); a contact no Set voice block touched is not listed.
 */
export const ContactVoicePayloadSchema = z.object({
  windowStart: z.string().datetime({ offset: true }),
  windowEnd: z.string().datetime({ offset: true }),
  contacts: z.array(VoiceHeardSchema.extend({ contact_id: z.string().min(1).max(256) })).max(100),
});
export type ContactVoicePayload = z.infer<typeof ContactVoicePayloadSchema>;

/**
 * What this environment's Connect instance actually has (requirements.md
 * 10.2), pushed on a schedule so the portal can offer real, human-readable
 * names when a profile is authored. Configuration metadata, not CX
 * content (ADR-0006) - names and ARNs, never a recording or a transcript.
 */
/**
 * ADR-0018 section 3. `settled`: no mutating Connect API call inside the debounce window; the inventory is as of
 * `settledAt`. `changing`: a burst is open - `burst` says how many calls, from which principal, since when. A
 * deploy is what a burst looks like; the state reports what it can see and the portal says the rest.
 */
export const InstanceStateSchema = z.object({
  state: z.enum(["settled", "changing"]),
  settledAt: z.string().datetime({ offset: true }).optional(),
  lastChangeAt: z.string().datetime({ offset: true }).optional(),
  burst: z.object({ calls: z.number().int().positive(), principal: z.string().max(512).optional(), firstCallAt: z.string().datetime({ offset: true }), lastCallAt: z.string().datetime({ offset: true }) }).optional(),
});
export type InstanceState = z.infer<typeof InstanceStateSchema>;

export const ConnectInventoryPayloadSchema = z.object({
  observedAt: z.string().datetime({ offset: true }),
  items: z
    .array(
      z.object({
        type: InventoryTypeSchema,
        name: z.string().min(1).max(256),
        arn: z.string().min(1).max(2048),
        resourceId: z.string().max(256).optional(),
        /** ADR-0019: for a hours of operation, its windows, zone and overrides as Connect has them now. */
        detail: HoursOfOperationDetailSchema.optional(),
        /** #352: for a flow a number routes to, what the lint in the customer's account found - findings only, never the flow. */
        findings: z.array(FlowFindingSchema).max(10).optional(),
        /** #352: for a phone number, the inbound flow it routes to. */
        flowArn: z.string().min(1).max(2048).optional(),
        /** #347: for a Lex bot alias, the log group ARN its conversation logs go to (text logs), when it has one. */
        logGroupArn: z.string().max(2048).optional(),
      }),
    )
    .max(500),
  /** An instance with thousands of queues arrives across several pushes. */
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
  /**
   * ADR-0018: an event-driven refresh carries only the types a burst of changes named; central replaces those
   * and leaves the rest. Empty with `instanceState` is a state-only push (a burst just opened). Absent (an
   * older template) means the whole inventory.
   */
  partial: z.array(InventoryTypeSchema).max(8).optional(),
  /** ADR-0018 section 3: whether the instance is settled or mid-change, and what the change looked like. */
  instanceState: InstanceStateSchema.optional(),
});
export type ConnectInventoryPayload = z.infer<typeof ConnectInventoryPayloadSchema>;

export const TemplateComponentSchema = z.enum([
  "EnableConnectAlarms",
  "EnableLexAlarms",
  "QuotaIncreaseRequestRole",
  "JourneyInspectorRole",
  "FlowConfiguration",
  "ContactRecords",
  "AgentTelemetry",
  "HoursOfOperationWrites",
]);
export type TemplateComponent = z.infer<typeof TemplateComponentSchema>;

export const CrossAccountSchema = z.object({
  quota_increase_role_arn: z.string().regex(/^arn:aws[\w-]*:iam::\d{12}:role\/.+$/).optional(),
  journey_inspector_role_arn: z.string().regex(/^arn:aws[\w-]*:iam::\d{12}:role\/.+$/).optional(),
  journey_cache_table: z.string().min(3).max(255).optional(),
  /** ADR-0015 section 4: the function in the customer's account that hashes a typed value with their salt. */
  value_hasher_arn: z.string().regex(/^arn:aws[\w-]*:lambda:[a-z0-9-]+:\d{12}:function:.+$/).optional(),
  /** ADR-0015 section 2: the contact record stream the forwarder reads - theirs, or the one the template attached. */
  contact_record_stream_arn: z.string().regex(/^arn:aws[\w-]*:kinesis:[a-z0-9-]+:\d{12}:stream\/.+$/).optional(),
  /** ADR-0015 section 11: the per-second series the agent extension fills, in the customer's account; the trace reads a contact's slice through the inspector role. */
  telemetry_series_table: z.string().min(3).max(255).optional(),
});
export type CrossAccount = z.infer<typeof CrossAccountSchema>;

/**
 * An Amazon Connect contact id: a lower-case UUID, the only form Connect
 * issues. Every boundary that takes one from a caller checks it here,
 * because the journey inspector writes it into Logs Insights query text
 * (#344); anything else never reaches AWS.
 */
export const ContactIdSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, "not an Amazon Connect contact id");
export type ContactId = z.infer<typeof ContactIdSchema>;

/**
 * ADR-0015 section 3: one record per contact, as the customer's forwarder
 * allow-lists it from the contact record stream. Every field here is
 * operational metadata or an opaque pointer; the caller's number and any
 * attribute the customer listed for hashing arrive only as HMACs whose
 * salt never leaves their account. `.strict()` is the allow-list from the
 * receiving side: a field Connect adds is refused until this schema
 * names it.
 */
const IsoSchema = z.string().datetime({ offset: true });
const AudioQualitySchema = z.object({ score: z.number().min(0).max(5).optional(), issues: z.array(z.string().max(64)).max(10) }).strict();

export const ContactRecordPayloadSchema = z
  .object({
    contact_id: z.string().min(1).max(256),
    initial_contact_id: z.string().max(256).optional(),
    previous_contact_id: z.string().max(256).optional(),
    next_contact_id: z.string().max(256).optional(),
    related_contact_id: z.string().max(256).optional(),
    channel: z.string().max(32),
    initiation_method: z.string().max(64),
    subtype: z.string().max(64).optional(),
    initiation_timestamp: IsoSchema,
    connected_to_system_timestamp: IsoSchema.optional(),
    disconnect_timestamp: IsoSchema.optional(),
    scheduled_timestamp: IsoSchema.optional(),
    last_update_timestamp: IsoSchema.optional(),
    disconnect_reason: z.string().max(64).optional(),
    potential_disconnect_issue: z.string().max(64).optional(),
    queue: z
      .object({ name: z.string().max(256), enqueue_timestamp: IsoSchema.optional(), dequeue_timestamp: IsoSchema.optional(), duration_seconds: z.number().int().nonnegative().optional() })
      .strict()
      .optional(),
    agent: z
      .object({
        /** The agent ARN - an opaque id; the username is never taken. */
        arn: z.string().max(512),
        connected_to_agent_timestamp: IsoSchema.optional(),
        interaction_duration_seconds: z.number().int().nonnegative().optional(),
        customer_hold_duration_seconds: z.number().int().nonnegative().optional(),
        longest_hold_duration_seconds: z.number().int().nonnegative().optional(),
        number_of_holds: z.number().int().nonnegative().optional(),
        after_contact_work_start_timestamp: IsoSchema.optional(),
        after_contact_work_end_timestamp: IsoSchema.optional(),
        after_contact_work_duration_seconds: z.number().int().nonnegative().optional(),
        routing_profile_name: z.string().max(256).optional(),
        hierarchy_group_names: z.array(z.string().max(256)).max(5).optional(),
        device: z.object({ platform: z.string().max(64).optional(), version: z.string().max(64).optional(), os: z.string().max(64).optional() }).strict().optional(),
      })
      .strict()
      .optional(),
    audio_quality: z.object({ agent: AudioQualitySchema.optional(), customer: AudioQualitySchema.optional() }).strict().optional(),
    /** The number the caller dialed - the customer's own published number, so the `dnis` slice. */
    system_endpoint: z.object({ address: z.string().max(64), type: z.string().max(32) }).strict().optional(),
    customer_endpoint_type: z.string().max(32).optional(),
    /** HMAC-SHA256 hex of the caller's endpoint, salt in the customer's account (ADR-0015 section 4). */
    caller_hash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
    transfer_completed_timestamp: IsoSchema.optional(),
    /** The kind of transfer target only - never an address. */
    transferred_to_type: z.string().max(32).optional(),
    recording: z.object({ status: z.string().max(32).optional(), type: z.string().max(32).optional() }).strict().optional(),
    /** Attributes the customer listed in ContactAttributesInClear: at most ten, values cut to 64 characters. */
    attributes: z.record(z.string().max(64), z.string().max(64)).refine((r) => Object.keys(r).length <= 10, "at most ten attributes").optional(),
    /** Attributes the customer listed in ContactAttributesHashed: key to HMAC hex. */
    attribute_hashes: z.record(z.string().max(64), z.string().regex(/^[a-f0-9]{64}$/)).refine((r) => Object.keys(r).length <= 10, "at most ten hashed attributes").optional(),
    /** ADR-0015 section 11: the shape of the call's audio and the agent's machine, from the series in the customer's account - never a second of it. */
    audio_summary: AudioSummarySchema.optional(),
    /** ADR-0015 section 9: the SHA-256 of the payload the forwarder logged, so what central holds can be checked against what left. */
    payload_sha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  })
  .strict();
export type ContactRecordPayload = z.infer<typeof ContactRecordPayloadSchema>;

/** See design.md "Heartbeat and template drift". Updates `Environments` only, never `Events`. */
export const HeartbeatPayloadSchema = z.object({
  template_version: z.string().min(1).max(64),
  stack_id: z.string().min(1),
  region: z.string().min(1),
  enabled_components: z.array(TemplateComponentSchema),
  /** Undelivered alarm events sitting in the forwarder's dead-letter queue. */
  forwarder_dlq_depth: z.number().int().nonnegative().optional(),
  /** ADR-0003 Track B inputs, declared in the customer's template and reported here so central knows where to look. */
  journey_config: z
    .object({
      /** #347: the flow log group in use - the override when one was set, otherwise `/aws/connect/<instance alias>`. */
      contact_flow_log_group: z.string().optional(),
      /** #347: the group derived from the instance, read when the preferred one has nothing; absent when it is the same group. */
      contact_flow_log_group_fallback: z.string().optional(),
      /** The customer's own additions (JourneyLogGroupArns), always in scope. */
      log_groups: z.array(z.string()).max(50).optional(),
      /** #347: whether the inspector may also read the Lambda and Lex log groups the inventory shows associated with the instance. */
      log_group_discovery: z.boolean().optional(),
      connect_instance_id: z.string().optional(),
      /** Hours the customer chose to keep a reconstructed call in their own cache table (ADR-0006). */
      cache_retention_hours: z.number().int().positive().max(168).optional(),
    })
    .optional(),
  /**
   * What the stack created for the opt-ins central reaches into their
   * account for. Names carry the stack name so several deployments can
   * share one account (one per Connect instance, #93); central assumes
   * and reads exactly what is reported here, never a composed name.
   */
  cross_account: CrossAccountSchema.optional(),
  /** ADR-0015 section 11: the endpoint the extension posts to and the newest extension version seen there. */
  agent_telemetry: z
    .object({
      endpoint: z.string().url(),
      retention_days: z.number().int().positive().max(400).optional(),
      extension_version: z.string().max(32).optional(),
      last_sample_at: z.string().datetime({ offset: true }).optional(),
    })
    .optional(),
});
export type HeartbeatPayload = z.infer<typeof HeartbeatPayloadSchema>;

/**
 * #272: what the customer's stack meters for the bill - Connect's own
 * billed usage, read from their Cost Explorer (exact) or, where a member
 * account cannot read it, from Connect's metrics (an approximation, and
 * said so). The raw lines travel too, so central can classify and
 * re-classify without a template round.
 */
export const UsageLineSchema = z.object({
  usage_type: z.string().min(1).max(128),
  quantity: z.number().nonnegative(),
  unit: z.string().max(32).optional(),
});
export const UsagePayloadSchema = z.object({
  /** YYYY-MM, the calendar month the lines belong to. */
  month: z.string().regex(/^\d{4}-\d{2}$/),
  source: z.enum(["cost-explorer", "connect-metrics"]),
  /** Cost Explorer lines for Amazon Connect, one per usage type. Empty on the metrics source. */
  lines: z.array(UsageLineSchema).max(200),
  /** The stack's own reading of the lines - central re-derives from the lines and prefers its own. */
  voice_minutes: z.number().nonnegative().optional(),
  chat_messages: z.number().nonnegative().optional(),
  emails: z.number().nonnegative().optional(),
  cases: z.number().nonnegative().optional(),
  as_of: z.string().datetime({ offset: true }),
  /** False while the month is still running. */
  complete: z.boolean(),
});
export type UsagePayload = z.infer<typeof UsagePayloadSchema>;

export const TestPayloadSchema = z.object({
  triggeredBy: z.literal("cloudformation-custom-resource"),
  template_version: z.string().min(1).max(64).optional(),
});
export type TestPayload = z.infer<typeof TestPayloadSchema>;

const BaseEnvelopeSchema = z.object({
  /**
   * Deterministic, not random: hash of aws_account_number + the source
   * identifier (alarm ARN, check ID, ...) + the source's own state-change
   * timestamp. The ingestion Lambda writes under a conditional put on
   * this, so SNS redelivery and forwarder retries collapse onto one item.
   */
  event_id: z.string().regex(/^[a-f0-9]{32}$/),
  friendly_name: z.string().min(1).max(256),
  aws_account_number: z.string().regex(/^\d{12}$/),
  /**
   * Set in the customer account, where the template knows which baseline
   * alarm fired. Absent for alarms a customer wired to the topic
   * themselves; the router falls back to a central lookup.
   */
  severity: SeveritySchema.optional(),
  timestamp: z.string().datetime({ offset: true }),
});

export const EventEnvelopeSchema = z.discriminatedUnion("type", [
  BaseEnvelopeSchema.extend({ type: z.literal("cloudwatch_alarm"), payload: CloudWatchAlarmPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("trusted_advisor"), payload: TrustedAdvisorPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("support_case"), payload: SupportCasePayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("service_quota"), payload: ServiceQuotaPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("concurrency_metrics"), payload: ConcurrencyMetricsPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("journey_rollup"), payload: JourneyRollupPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("connect_inventory"), payload: ConnectInventoryPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("contact_record"), payload: ContactRecordPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("contact_voice"), payload: ContactVoicePayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("heartbeat"), payload: HeartbeatPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("usage"), payload: UsagePayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("test"), payload: TestPayloadSchema }),
]);
export type EventEnvelope = z.infer<typeof EventEnvelopeSchema>;

/** Shape of every error the ingestion API returns (CLAUDE.md section 12). */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    request_id: z.string(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const IngestAcceptedSchema = z.object({
  event_id: z.string(),
  request_id: z.string(),
  duplicate: z.boolean(),
});
export type IngestAccepted = z.infer<typeof IngestAcceptedSchema>;
